import React, { useCallback, useEffect, useState } from 'react';
import { AccountBalanceWallet, HourglassEmptyOutlined, ReceiptLongOutlined } from '@mui/icons-material';
import comms from '../../comms';
import ScheduleDropDialog from './ScheduleDropDialog';
import { fmtMoney, fmtDate } from './cashieringUtils';

// Cashiering screen (docs/DriverRequirement/05_cashiering_and_financials (1).md,
// 08_driverapp_cashiering_handoff.md): a single sequential view — available
// balance, pending total, and a trip settlement list — backed by
// POST /API/Driver/Cashiering. No Today/History tabs. Tapping a trip opens a
// drilldown using only the fields the API already supplies (tripNo, when,
// amount, status) — there is no separate receipt endpoint for this screen.
function Cashiering({ userSettings }) {
  const driverId =
    userSettings?.Id || userSettings?.id || JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id;

  const [state, setState] = useState({ status: 'loading', available: null, pending: null, trips: [] });
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showScheduleDrop, setShowScheduleDrop] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!driverId) {
      setState({ status: 'error', available: null, pending: null, trips: [] });
      return;
    }
    setState((prev) => ({ ...prev, status: 'loading' }));
    try {
      const res = await comms.getDriverCashiering(driverId);
      if (!res || res.status === false) {
        setState({ status: 'error', available: null, pending: null, trips: [], message: res?.message });
        return;
      }
      setState({
        status: 'loaded',
        available: Number(res.available) || 0,
        pending: Number(res.pending) || 0,
        trips: Array.isArray(res.trips) ? res.trips : [],
      });
    } catch (err) {
      setState({ status: 'error', available: null, pending: null, trips: [], message: err?.message });
    }
  }, [driverId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4">
      {/* Balance card */}
      <div className="rounded-2xl bg-gradient-to-br from-blue-800 to-blue-600 px-5 py-5 text-white shadow-sm">
        <div className="flex items-center gap-2 text-blue-100">
          <AccountBalanceWallet style={{ fontSize: 20 }} />
          <span className="text-sm font-medium">Available Balance</span>
        </div>
        <div className="mt-3 text-3xl font-bold tracking-tight">
          {state.status === 'loaded' ? fmtMoney(state.available) ?? '$0.00' : '—'}
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-sm text-blue-100">
          <HourglassEmptyOutlined style={{ fontSize: 16 }} />
          <span>Pending: {state.status === 'loaded' ? fmtMoney(state.pending) ?? '$0.00' : '—'}</span>
        </div>
      </div>

      <button
        onClick={() => setShowScheduleDrop(true)}
        disabled={state.status !== 'loaded' || !state.available}
        className="w-full rounded-2xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-transform active:scale-95 disabled:opacity-50"
      >
        Schedule ACH Drop
      </button>

      {state.status === 'loading' && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-2xl bg-gray-200" />
          ))}
        </div>
      )}

      {state.status === 'error' && (
        <div className="rounded-2xl bg-white px-4 py-6 text-center text-sm text-gray-500 shadow-sm">
          {state.message || 'Unable to load cashiering.'}
        </div>
      )}

      {state.status === 'loaded' && (
        <div className="space-y-2">
          <h3 className="px-1 text-sm font-semibold text-gray-700">Trips</h3>
          {!state.trips.length && (
            <div className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
              No trips found.
            </div>
          )}
          {state.trips.map((trip, i) => {
            const isPending = trip.status === 'pending';
            return (
              <button
                key={trip.tripNo ?? i}
                onClick={() => setSelectedTrip(trip)}
                className="flex w-full items-center gap-3 rounded-2xl bg-white px-4 py-3 text-left shadow-sm"
              >
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-slate-50">
                  <ReceiptLongOutlined style={{ fontSize: 20, color: '#475569' }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm text-gray-800">Trip #{trip.tripNo ?? '—'}</span>
                    <span className="flex-shrink-0 text-sm font-semibold text-gray-900">
                      {fmtMoney(trip.amount) ?? '—'}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-gray-500">
                    <span>{fmtDate(trip.when)}</span>
                    <span
                      className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                      style={
                        isPending
                          ? { background: '#FFFBEB', color: '#B45309' }
                          : { background: '#ECFDF5', color: '#059669' }
                      }
                    >
                      {isPending ? 'Pending' : 'Settled'}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Trip drilldown — fields supplied directly by the Cashiering API, no separate fetch */}
      {selectedTrip && (
        <div className="fixed inset-0 z-[2000] flex items-end justify-center bg-black/40 sm:items-center">
          <div className="w-full max-w-md rounded-t-2xl bg-white p-4 shadow-lg sm:rounded-2xl">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Trip #{selectedTrip.tripNo ?? '—'}</h3>
              <button
                onClick={() => setSelectedTrip(null)}
                className="text-sm font-semibold text-blue-700"
              >
                Close
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Date</span>
                <span className="font-medium text-gray-900">{fmtDate(selectedTrip.when)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Amount</span>
                <span className="font-medium text-gray-900">{fmtMoney(selectedTrip.amount) ?? '—'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Status</span>
                <span className="font-medium text-gray-900">
                  {selectedTrip.status === 'pending' ? 'Pending' : 'Settled'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <ScheduleDropDialog
        open={showScheduleDrop}
        driverId={driverId}
        available={state.available}
        onClose={() => setShowScheduleDrop(false)}
        onScheduled={(res) => {
          setShowScheduleDrop(false);
          setToast(`ACH drop of ${fmtMoney(res.scheduledAmount)} scheduled`);
          load();
          setTimeout(() => setToast(null), 3500);
        }}
      />

      {toast && (
        <div className="fixed inset-x-0 bottom-6 z-[2100] flex justify-center px-4">
          <div className="rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white shadow-lg">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

export default Cashiering;
